import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, FileDown, CheckCircle2, XCircle, X } from 'lucide-react'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/ui/page-header'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table'
import { Dropdown, DropdownItem } from '@/components/ui/dropdown'
import AsyncSelect from '@/components/crud/AsyncSelect'
import { formatBRL, formatDate } from '@/lib/utils'

const diasFiltros = [15, 30, 60, 90]

/** Cor do badge de "dias para aditivar" conforme urgência. */
function diasBadge(dias) {
  if (dias === null || dias === undefined) return null
  if (dias < 0) return <Badge variant="destructive">Vencido</Badge>
  let variant = 'muted'
  if (dias <= 15) variant = 'destructive'
  else if (dias <= 30) variant = 'warning'
  else if (dias <= 60) variant = 'default'
  return <Badge variant={variant}>{dias} dias</Badge>
}

export default function ContratoList() {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)

  const [q, setQ] = useState('')
  const [clienteID, setClienteID] = useState('')
  const [clienteLabel, setClienteLabel] = useState('')
  const [modalidadeID, setModalidadeID] = useState('')
  const [assinadoCliente, setAssinadoCliente] = useState('')
  // A listagem abre já filtrada pelos contratos ativos (padrão do dia a dia).
  const [ativo, setAtivo] = useState('1')
  const [dias, setDias] = useState('')
  const [modalidades, setModalidades] = useState([])

  const scrollRef = useRef(null)

  useEffect(() => {
    api.get('/modalidades', { params: { limit: 100 } }).then(({ data }) => setModalidades(data.data))
  }, [])

  const buildParams = useCallback(
    (nextOffset) => ({
      q,
      clienteID,
      modalidadeID,
      assinadoCliente,
      ativo,
      dias,
      limit: 30,
      offset: nextOffset,
    }),
    [q, clienteID, modalidadeID, assinadoCliente, ativo, dias]
  )

  const load = useCallback(
    async (reset) => {
      if (loading) return
      setLoading(true)
      try {
        const nextOffset = reset ? 0 : offset
        const { data } = await api.get('/contratos', { params: buildParams(nextOffset) })
        setItems((prev) => (reset ? data.data : [...prev, ...data.data]))
        setOffset(data.offset)
        setHasMore(data.hasMore)
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildParams, offset, loading]
  )

  // Recarrega ao alterar filtros (debounce da busca).
  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0)
      load(true)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, clienteID, modalidadeID, assinadoCliente, ativo, dias])

  function onScroll(e) {
    const el = e.target
    if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      load(false)
    }
  }

  // "Limpar" volta ao estado inicial da tela, que já traz apenas os contratos ativos.
  const hasFilters = q || clienteID || modalidadeID || assinadoCliente !== '' || ativo !== '1' || dias
  function clearFilters() {
    setQ(''); setClienteID(''); setClienteLabel(''); setModalidadeID('')
    setAssinadoCliente(''); setAtivo('1'); setDias('')
  }

  function baixarRelatorio(tipo) {
    const params = new URLSearchParams(buildParams(0))
    params.set('tipo', tipo)
    window.open(`/api/contratos/relatorio?${params.toString()}`, '_blank')
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Contratos"
        subtitle="Compras, contratos e aditivos."
        actions={
          <>
            <Dropdown
              align="right"
              trigger={<Button variant="outline"><FileDown className="h-4 w-4" /> Relatórios</Button>}
            >
              <DropdownItem onClick={() => baixarRelatorio('renovacoes')}>Renovações de contrato</DropdownItem>
              <DropdownItem onClick={() => baixarRelatorio('faturamentos')}>Faturamentos</DropdownItem>
            </Dropdown>
            <Button onClick={() => navigate('/contrato/novo')}><Plus className="h-4 w-4" /> Novo</Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar contrato, cliente, processo..." className="pl-9" />
          </div>

          <div className="w-56">
            <AsyncSelect
              endpoint="/clientes"
              value={clienteID}
              onChange={(v) => setClienteID(v)}
              optionValue="clienteID"
              optionLabel="nome"
              placeholder="Cliente"
              initialLabel={clienteLabel}
            />
          </div>

          <Select value={modalidadeID} onChange={(e) => setModalidadeID(e.target.value)} className="w-44">
            <option value="">Modalidade</option>
            {modalidades.map((m) => (
              <option key={m.modalidadeID} value={m.modalidadeID}>{m.nome}</option>
            ))}
          </Select>

          <Select value={assinadoCliente} onChange={(e) => setAssinadoCliente(e.target.value)} className="w-40">
            <option value="">Ass. cliente: todos</option>
            <option value="1">Assinado</option>
            <option value="0">Não assinado</option>
          </Select>

          <Select value={ativo} onChange={(e) => setAtivo(e.target.value)} className="w-36">
            <option value="">Status: todos</option>
            <option value="1">Ativos</option>
            <option value="0">Inativos</option>
          </Select>

          <div className="flex items-center gap-1">
            {diasFiltros.map((d) => (
              <button
                key={d}
                onClick={() => setDias((cur) => (String(cur) === String(d) ? '' : String(d)))}
                className={
                  'px-2.5 h-9 rounded-md text-sm border transition-colors ' +
                  (String(dias) === String(d)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-accent/60')
                }
                title={`Aditivar em até ${d} dias`}
              >
                {d}d
              </button>
            ))}
          </div>

          {hasFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4" /> Limpar
            </Button>
          )}
        </div>
      </PageHeader>

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-auto p-6 pt-4">
        <Table>
          <THead>
            <tr>
              <TH>Cliente</TH>
              <TH>Nº Contrato</TH>
              <TH>Modalidade</TH>
              <TH>Fim contrato</TH>
              <TH>Fim adit. valor</TH>
              <TH>Fim adit. prazo</TH>
              <TH>Aditivar</TH>
              <TH className="text-right">Valor</TH>
              <TH className="text-center">Ass.</TH>
              <TH className="text-center">Status</TH>
            </tr>
          </THead>
          <TBody>
            {items.map((c, i) => (
              <TR key={c.contratoID} index={i} clickable onClick={() => navigate(`/contrato/${c.contratoID}`)}>
                <TD>
                  <div className="font-medium">{c.clienteNome}</div>
                  <div className="text-xs text-muted-foreground">{c.clienteCnpj}</div>
                </TD>
                <TD>{c.numeroContrato}</TD>
                <TD>{c.modalidadeNome}</TD>
                <TD>{formatDate(c.dataFimContrato)}</TD>
                <TD>{c.dataFimAditivoValor ? formatDate(c.dataFimAditivoValor) : <span className="text-muted-foreground">—</span>}</TD>
                <TD>{c.dataFimAditivoPrazo ? formatDate(c.dataFimAditivoPrazo) : <span className="text-muted-foreground">—</span>}</TD>
                <TD>{diasBadge(c.diasParaAditivar)}</TD>
                <TD className="text-right">{formatBRL(c.valorTotal)}</TD>
                <TD className="text-center">
                  {c.assinadoCliente ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground inline" />
                  )}
                </TD>
                <TD className="text-center">
                  {c.ativo ? <Badge variant="success">Ativo</Badge> : <Badge variant="muted">Inativo</Badge>}
                </TD>
              </TR>
            ))}
            {items.length === 0 && !loading && (
              <tr>
                <TD colSpan={10} className="text-center text-muted-foreground py-10">Nenhum contrato encontrado.</TD>
              </tr>
            )}
          </TBody>
        </Table>
        {loading && <p className="text-center text-sm text-muted-foreground py-4">Carregando...</p>}
      </div>
    </div>
  )
}
